import { useTranslation } from '../../../application/hooks/useTranslation';
import { formatDhakaDate } from '../../../domain/formatters/date';
import type { SupportedLocale } from '../../../domain/formatters/currency';
import type { CommissionLedgerEntry } from '../../../domain/entities/CommissionLedgerEntry';
import { AmountDisplay } from '../ui/AmountDisplay';
import { Badge } from '../ui/Badge';
import { typeLabel } from './ledgerLabels';
import './commissions.css';

interface RowProps {
  entry: CommissionLedgerEntry;
  locale: SupportedLocale;
  onSelect: (entry: CommissionLedgerEntry) => void;
}

/** One desktop table row — metric limit: row components ≤ 100 lines. */
function LedgerRow({ entry, locale }: RowProps) {
  const { t } = useTranslation();
  const label = typeLabel(entry, t, locale);
  return (
    <tr data-testid="ledger-row">
      <td>{formatDhakaDate(entry.createdAt, locale)}</td>
      <td>{entry.sourceUserName}</td>
      <td>
        <Badge
          tone={entry.poolType === 'BOOKING_POOL' ? 'info' : 'accent'}
          role="img"
          aria-label={t('commissions.typeAria', { type: label })}
        >
          {label}
        </Badge>
      </td>
      <td><AmountDisplay amount={entry.amount} /></td>
    </tr>
  );
}

/** One mobile (<768px) stacked card; the whole card opens the detail sheet. */
function LedgerCard({ entry, locale, onSelect }: RowProps) {
  const { t } = useTranslation();
  return (
    <button type="button" className="ledger-card tap" onClick={() => onSelect(entry)} data-testid="ledger-card">
      <span className="row-between">
        <strong>{entry.sourceUserName}</strong>
        <AmountDisplay amount={entry.amount} bold />
      </span>
      <span className="muted">
        {typeLabel(entry, t, locale)} · {formatDhakaDate(entry.createdAt, locale)}
      </span>
    </button>
  );
}

export interface CommissionLedgerTableProps {
  entries: CommissionLedgerEntry[];
  onSelect: (entry: CommissionLedgerEntry) => void;
}

/**
 * Ledger rendered twice: desktop table (`hide-mobile`) and touch-friendly stacked cards
 * (`show-mobile`), so the <768px transform needs no JS.
 */
export function CommissionLedgerTable({ entries, onSelect }: CommissionLedgerTableProps) {
  const { t, locale } = useTranslation();
  if (entries.length === 0) return null;

  return (
    <>
      <div className="table-wrap hide-mobile">
        <table className="data">
          <caption className="sr-only">{t('commissions.caption')}</caption>
          <thead>
            <tr>
              <th scope="col">{t('commissions.colDate')}</th>
              <th scope="col">{t('commissions.source')}</th>
              <th scope="col">{t('commissions.colFor')}</th>
              <th scope="col">{t('commissions.colAmount')}</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <LedgerRow key={entry.id} entry={entry} locale={locale} onSelect={onSelect} />
            ))}
          </tbody>
        </table>
      </div>
      <div className="show-mobile ledger-cards">
        {entries.map((entry) => (
          <LedgerCard key={entry.id} entry={entry} locale={locale} onSelect={onSelect} />
        ))}
      </div>
    </>
  );
}
